import { makeAutoObservable, runInAction } from 'mobx';
import {
  getWaitlistQuestionnaireDisplay,
  parseWaitlistQuestionnaire,
  type WaitlistQuestionnaireDisplay,
} from '@/pages/InviteCode/waitlistQuestionnaire';
import { inviteCodeApi } from '@/services/inviteCodeApi';
import { brandStore } from './brandStore';
import type {
  InviteCodeApplication as ApiApplication,
  MailOutbox as ApiMailOutbox,
  WaitlistQuestionnaire,
} from '@/types';
import { InviteApplicationStatus, MailOutboxStatus } from '@/types';

export interface MailOutbox {
  id: string;
  bizType: string;
  bizId: string;
  templateCode: string;
  toEmail: string;
  payload: string;
  status: 'pending' | 'sent' | 'failed' | 'unknown';
  retryCount: number;
  providerMessageId?: string;
  lastError?: string;
  sentAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

// 前端使用的申请类型（camelCase + 字符串状态）
export interface InviteCodeApplication {
  id: string;
  email: string;
  reason: string;
  questionnaire?: WaitlistQuestionnaire | null;
  questionnaireDisplay?: WaitlistQuestionnaireDisplay | null;
  status: 'pending' | 'email_sent' | 'rejected';
  inviteCodeId?: string;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  latestMailOutbox?: MailOutbox;
}

function convertApplicationStatus(
  status: InviteApplicationStatus | keyof typeof InviteApplicationStatus | string,
): 'pending' | 'email_sent' | 'rejected' {
  const normalizedStatus = String(status);

  if (
    status === InviteApplicationStatus.INVITE_APPLICATION_STATUS_PENDING ||
    normalizedStatus === 'INVITE_APPLICATION_STATUS_PENDING' ||
    normalizedStatus === '1'
  ) {
    return 'pending';
  }
  if (
    status === InviteApplicationStatus.INVITE_APPLICATION_STATUS_EMAIL_SENT ||
    normalizedStatus === 'INVITE_APPLICATION_STATUS_EMAIL_SENT' ||
    normalizedStatus === '2'
  ) {
    return 'email_sent';
  }
  if (
    status === InviteApplicationStatus.INVITE_APPLICATION_STATUS_REJECTED ||
    normalizedStatus === 'INVITE_APPLICATION_STATUS_REJECTED' ||
    normalizedStatus === '3'
  ) {
    return 'rejected';
  }
  return 'pending';
}

function convertMailOutboxStatus(
  status: MailOutboxStatus | keyof typeof MailOutboxStatus | string,
): 'pending' | 'sent' | 'failed' | 'unknown' {
  const normalizedStatus = String(status);

  if (
    status === MailOutboxStatus.MAIL_OUTBOX_STATUS_PENDING ||
    normalizedStatus === 'MAIL_OUTBOX_STATUS_PENDING' ||
    normalizedStatus === '1'
  ) {
    return 'pending';
  }
  if (
    status === MailOutboxStatus.MAIL_OUTBOX_STATUS_SENT ||
    normalizedStatus === 'MAIL_OUTBOX_STATUS_SENT' ||
    normalizedStatus === '2'
  ) {
    return 'sent';
  }
  if (
    status === MailOutboxStatus.MAIL_OUTBOX_STATUS_FAILED ||
    normalizedStatus === 'MAIL_OUTBOX_STATUS_FAILED' ||
    normalizedStatus === '3'
  ) {
    return 'failed';
  }
  return 'unknown';
}

function transformMailOutbox(api?: ApiMailOutbox): MailOutbox | undefined {
  if (!api) {
    return undefined;
  }
  return {
    id: api.id,
    bizType: api.biz_type,
    bizId: api.biz_id,
    templateCode: api.template_code,
    toEmail: api.to_email,
    payload: api.payload,
    status: convertMailOutboxStatus(api.status),
    retryCount: api.retry_count,
    providerMessageId: api.provider_message_id,
    lastError: api.last_error,
    sentAt: api.sent_at,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformApplication(api: ApiApplication): InviteCodeApplication {
  const questionnaire = parseWaitlistQuestionnaire(api.reason, api.questionnaire);

  return {
    id: api.id,
    email: api.email,
    reason: api.reason,
    questionnaire,
    questionnaireDisplay: getWaitlistQuestionnaireDisplay(questionnaire),
    status: convertApplicationStatus(api.status),
    inviteCodeId: api.invite_code_id,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    userId: api.user_id,
    latestMailOutbox: transformMailOutbox(api.latest_mail_outbox),
  };
}

class InviteApplicationStore {
  applications: InviteCodeApplication[] = [];
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  statusFilter: InviteApplicationStatus | undefined = undefined;
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  private async fetchInviteApplicationsForApp(appId: string) {
    const response = await inviteCodeApi.listInviteApplications({
      app_id: appId,
      page_num: this.pagination.page,
      page_size: this.pagination.pageSize,
      status: this.statusFilter,
    });

    runInAction(() => {
      this.applications = response.applications.map(transformApplication);
      this.pagination.total = response.total;
      this.loading = false;
    });
  }

  async fetchInviteApplications() {
    const appId = brandStore.requireActiveBrandAppId();
    this.loading = true;
    try {
      await this.fetchInviteApplicationsForApp(appId);
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async approveAndSendInvite(id: string, inviteCodeId?: string) {
    const appId = brandStore.requireActiveBrandAppId();
    this.loading = true;
    try {
      await inviteCodeApi.approveAndSendInvite({
        app_id: appId,
        id,
        invite_code_id: inviteCodeId,
      });
      await this.fetchInviteApplicationsForApp(appId);
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  setStatusFilter(status: InviteApplicationStatus | undefined) {
    this.statusFilter = status;
    this.pagination.page = 1;
  }

  setPagination(page: number, pageSize?: number) {
    this.pagination.page = page;
    if (pageSize !== undefined) {
      this.pagination.pageSize = pageSize;
    }
  }
}

export const inviteApplicationStore = new InviteApplicationStore();
